import { useState } from "react";
import { compareSectorNames } from "@/lib/sectorSort";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { DIST_PORTFOLIOS, distLabel } from "./distConfig";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface Commentary { id: string; portfolio: number; sector_id: string; comment: string | null; previous_comment: string | null; last_updated: string; }

export default function DistSectorCommentary() {
  const queryClient = useQueryClient();
  const [selectedPortfolio, setSelectedPortfolio] = useState("1");
  const [edited, setEdited] = useState<Record<string, string>>({});
  const portfolio = parseInt(selectedPortfolio);

  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("*").order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });
  const { data: commentary } = useQuery({
    queryKey: ["ba-dist-commentary", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_dist_sector_commentary").select("*").eq("portfolio", portfolio);
      if (error) throw error;
      return data as Commentary[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ sectorId, comment }: { sectorId: string; comment: string }) => {
      const existing = commentary?.find((c) => c.sector_id === sectorId);
      const { error } = await supabase.from("ba_dist_sector_commentary").upsert({
        portfolio, sector_id: sectorId, comment, previous_comment: existing?.comment || null,
        last_updated: new Date().toISOString().split("T")[0],
      }, { onConflict: "portfolio,sector_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-dist-commentary", portfolio] });
      toast.success("Commentary saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sortedSectors = [...(sectors ?? [])].sort((a, b) => compareSectorNames(a.name, b.name));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dist Sector Commentary</h1>
        <p className="text-sm text-muted-foreground">Investment rationale by sector and Dist portfolio</p>
      </div>
      <Tabs value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
        <TabsList>
          {DIST_PORTFOLIOS.map((p) => (
            <TabsTrigger key={p} value={String(p)}>{distLabel(p)}</TabsTrigger>
          ))}
        </TabsList>
        {DIST_PORTFOLIOS.map((p) => (
          <TabsContent key={p} value={String(p)} className="space-y-4 mt-4">
            <Card>
              <CardContent className="space-y-3 pt-6">
                {sortedSectors.map((sector) => {
                  const c = commentary?.find((x) => x.sector_id === sector.id);
                  const key = `${portfolio}-${sector.id}`;
                  const currentText = edited[key] ?? c?.comment ?? "";
                  return (
                    <div key={sector.id} className="border rounded-lg p-3 space-y-2">
                      <span className="font-medium text-sm">{sector.name}</span>
                      <Textarea value={currentText} onChange={(e) => setEdited({ ...edited, [key]: e.target.value })} placeholder="Enter investment rationale..." className="text-xs min-h-[60px]" />
                      <div className="flex items-center justify-end">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => saveMutation.mutate({ sectorId: sector.id, comment: currentText })}>
                          <Save className="h-3 w-3 mr-1" /> Save
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
